import type { ShapeType, TextStyle } from '../state/types';
import { COLORS, FONT_FAMILIES, LINE_HEIGHT_RATIO, PADDING } from '../../../config/constants';
import { getConnectorLabelTheme } from '../connector/ConnectorLabelLayout';

export interface ShapeOverlayOptions {
  value: string;
  left: number;
  top: number;
  width: number;
  height: number;
  initialFontSize: number;
  textStyle: TextStyle;
  shapeType: ShapeType;
  verticalPadding: number;
  inverted: boolean;
}

export interface ConnectorOverlayOptions {
  value: string;
  left: number;
  top: number;
  width: number;
  height: number;
  fontSize: number;
  inverted: boolean;
}

export interface TextElementOverlayOptions {
  value: string;
  placeholder?: string;
  left: number;
  top: number;
  width: number;
  height: number;
  fontSize: number;
  textStyle: TextStyle;
  inverted: boolean;
  wrapText?: boolean;
  scale?: number;
}

interface OverlayStyleOptions {
  left: number;
  top: number;
  width: number;
  height: number;
  fontFamily: string;
  fontSize: number;
  textAlign: string;
  lineHeight: number;
  background: string;
  border: string;
  color: string;
  fontWeight?: string;
  fontStyle?: string;
  caretColor?: string;
  padding?: string;
  borderRadius?: string;
  whiteSpace?: string;
  extra?: string;
}

const createTextOverlay = (
  value: string,
  styles: string,
  placeholder?: string
): HTMLTextAreaElement => {
  const input = document.createElement('textarea');
  input.value = value;
  if (placeholder) {
    input.placeholder = placeholder;
  }
  input.style.cssText = styles;
  return input;
};

const buildOverlayStyles = (options: OverlayStyleOptions): string => {
  const fontWeight = options.fontWeight ? `font-weight: ${options.fontWeight};` : '';
  const fontStyle = options.fontStyle ? `font-style: ${options.fontStyle};` : '';
  const caretColor = options.caretColor ? `caret-color: ${options.caretColor};` : '';
  const padding = options.padding ? `padding: ${options.padding};` : '';
  const borderRadius = options.borderRadius ? `border-radius: ${options.borderRadius};` : '';
  const whiteSpace = options.whiteSpace ? `white-space: ${options.whiteSpace};` : '';
  const extra = options.extra ?? '';

  return `
    position: fixed;
    left: ${options.left}px;
    top: ${options.top}px;
    width: ${options.width}px;
    height: ${options.height}px;
    font-family: ${options.fontFamily};
    font-size: ${options.fontSize}px;
    ${fontWeight}
    ${fontStyle}
    text-align: ${options.textAlign};
    line-height: ${options.lineHeight};
    background: ${options.background};
    border: ${options.border};
    outline: none;
    resize: none;
    box-sizing: border-box;
    color: ${options.color};
    ${caretColor}
    z-index: 1000;
    overflow: hidden;
    ${padding}
    ${borderRadius}
    ${whiteSpace}
    ${extra}
  `;
};

export const createShapeTextOverlay = (options: ShapeOverlayOptions): HTMLTextAreaElement => {
  const shapeStyles = getShapeTextInputStyles(
    options.shapeType,
    options.width,
    options.height,
    options.verticalPadding
  );

  const styles = buildOverlayStyles({
    left: options.left,
    top: options.top,
    width: options.width,
    height: options.height,
      fontFamily: options.textStyle.fontFamily,
    fontSize: options.initialFontSize,
    fontWeight: options.textStyle.fontWeight,
    fontStyle: options.textStyle.fontStyle,
    textAlign: options.textStyle.textAlign,
    lineHeight: LINE_HEIGHT_RATIO,
    background: 'transparent',
    border: `2px solid ${COLORS.accent}`,
    color: options.textStyle.color,
    caretColor: options.inverted ? COLORS.accentLight : COLORS.accent,
    extra: `
      word-wrap: break-word;
      overflow-wrap: break-word;
      ${shapeStyles}
    `,
  });

  return createTextOverlay(options.value, styles);
};

export const createConnectorTextOverlay = (
  options: ConnectorOverlayOptions
): HTMLTextAreaElement => {
  const theme = getConnectorLabelTheme(options.inverted);
  const bgColor = theme.bgColor;
  const textColor = theme.textColor;
  const borderColor = theme.borderColor;

  const styles = buildOverlayStyles({
    left: options.left,
    top: options.top,
    width: options.width,
    height: options.height,
    fontFamily: FONT_FAMILIES.connector,
    fontSize: options.fontSize,
    textAlign: 'center',
    lineHeight: LINE_HEIGHT_RATIO,
    background: bgColor,
    border: `2px solid ${borderColor}`,
    borderRadius: '4px',
    padding: '4px 8px',
    color: textColor,
  });

  return createTextOverlay(options.value, styles);
};

export const createTextElementOverlay = (
  options: TextElementOverlayOptions
): HTMLTextAreaElement => {
  const scale = options.scale ?? 1;
  const wrapExtra = options.wrapText ? 'word-wrap: break-word; overflow-wrap: break-word;' : '';
  // Use CSS transform: scale() to match canvas ctx.scale() exactly.
  // This avoids sub-pixel rounding differences at fractional zoom levels.
  const transformExtra = `transform-origin: top left; transform: scale(${scale});`;
  const styles = buildOverlayStyles({
    left: options.left,
    top: options.top,
    width: options.width,
    height: options.height,
    fontFamily: options.textStyle.fontFamily,
    fontSize: options.fontSize,
    fontWeight: options.textStyle.fontWeight,
    fontStyle: options.textStyle.fontStyle,
    textAlign: options.textStyle.textAlign,
    lineHeight: LINE_HEIGHT_RATIO,
    background: 'transparent',
    border: 'none',
    padding: '0',
    color: options.textStyle.color,
    caretColor: options.inverted ? COLORS.accentLight : COLORS.accent,
    whiteSpace: options.wrapText ? 'pre-wrap' : 'pre',
    extra: `${wrapExtra} ${transformExtra}`,
  });

  return createTextOverlay(options.value, styles, options.placeholder);
};

export const removeTextOverlay = (input: HTMLTextAreaElement | null): void => {
  if (input?.isConnected) {
    input.remove();
  }
};

const getShapeTextInputStyles = (
  shapeType: ShapeType,
  width: number,
  height: number,
  verticalPadding: number
): string => {
  const basePadding = PADDING.shapeText;

  switch (shapeType) {
    case 'ellipse': {
      const ellipsePadX = width * 0.15;
      const ellipsePadY = height * 0.15;
      return `
        clip-path: ellipse(50% 50%);
        border-radius: 50%;
        padding: ${Math.max(ellipsePadY, verticalPadding)}px ${ellipsePadX}px;
      `;
    }

    case 'diamond': {
      const diamondPadX = width * 0.25;
      const diamondPadY = height * 0.25;
      return `
        clip-path: polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%);
        padding: ${Math.max(diamondPadY, verticalPadding)}px ${diamondPadX}px;
      `;
    }

    case 'triangle': {
      const trianglePadX = width * 0.2;
      const trianglePadTop = height * 0.35;
      return `
        clip-path: polygon(50% 0%, 100% 100%, 0% 100%);
        padding: ${trianglePadTop}px ${trianglePadX}px ${basePadding}px ${trianglePadX}px;
      `;
    }

    case 'cylinder': {
      const cylinderPadY = height * 0.12;
      return `
        border-radius: ${width * 0.5}px / ${cylinderPadY}px;
        padding: ${Math.max(cylinderPadY, verticalPadding)}px ${basePadding}px;
      `;
    }

    case 'rectangle':
    default:
      return `
        border-radius: 8px;
        padding: ${verticalPadding}px ${basePadding}px;
      `;
  }
};
